<template>
  <td ref="popoverAnchor" :class="{showingPopover, isCellSelected}" :style="{background: cellColor}" @click="togglePopover()" v-bind="$attrs">
    <span :style="{ 'visibility' : isCellVisible ? 'visible' : 'hidden' }">
      <OptionalSettingCellContent :tag>
        <slot></slot>
      </OptionalSettingCellContent>
    </span>
  </td>
  <slot name="OverlayOverride" :helpText>
    <template v-if="showingDesktopUi">
      <Popover ref="popover" class="tableCell-popover" v-model:showingPopover="showingPopover" :popoverAnchor="popoverAnchor" :popoverPadding="popoverPadding">
        <OptionalSettingPopoverContent
          :tag
          :popoverTitle
          :selectedCells
          v-model:showingPopover="showingPopover"
          v-model:columnSelectionCode="columnSelectionCode"
        >
          <slot name="PopoverContentDesktop" :helpText></slot>
        </OptionalSettingPopoverContent>
      </Popover>
    </template>
    <template v-else>
      <slot name="DialogOverrideMobile">
        <Dialog modal dismissableMask v-model:visible="showingPopover" :closable="false" :draggable="false" :showHeader="false"
          :contentStyle="{ overflow: popoverScroll ? 'auto' : 'visible' }"
        >
          <OptionalSettingPopoverContent
            :tag
            :popoverTitle
            :selectedCells
            v-model:showingPopover="showingPopover"
            v-model:columnSelectionCode="columnSelectionCode"
          >
            <template v-if="$slots.DialogContentMobile">
              <slot name="DialogContentMobile" :helpText></slot>
            </template>
            <template v-else>
              <slot name="PopoverContentDesktop" :helpText></slot>
            </template>
          </OptionalSettingPopoverContent>
        </Dialog>
      </slot>
    </template>
  </slot>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { showingDesktopUi } from "./store.js";
import Popover from "./Popover.vue";
import OptionalSettingCellContent from "./OptionalSettingCellContent.vue";
import OptionalSettingPopoverContent from "./OptionalSettingPopoverContent.vue";

let props = defineProps({
  tag: {},
  cellColor: {},
  showingPopover: {},
  popoverDisabled: {},
  popoverTitle: {},
  popoverScroll: { type: Boolean, default: true },
  popoverPadding: {},
  columnSelectionCode: {},
  selectedCells: {}
});

let showingPopover = defineModel("showingPopover");
let columnSelectionCode = defineModel("columnSelectionCode");

let isCellVisible = computed(() => {
  return props.tag.committed && !props.tag.$session.timedOut;
});
watch(isCellVisible, () => {
  showingPopover.value = false;
});

let isCellSelected = computed(() => {
  return props.selectedCells?.map(cell => cell.componentArgs.tag).includes(props.tag);
});

let popover = ref(null);
let popoverAnchor = ref(null);

function togglePopover() {
  if (isCellVisible.value && !props.popoverDisabled)
    showingPopover.value = !showingPopover.value;
}

let helpText = ref();
watch(showingPopover, () => {
  let tag = props.tag;
  let session = tag.$session;
  if (showingPopover.value) {
    let tagHandle = parseInt(tag.handle); // Parse removes any -clone suffix if present on the handle.
    session.startPollingFor("helpText", { tagHandle }, result => {
      helpText.value = result;
    });
  }
  else {
    session.stopPollingFor("helpText");
    helpText.value = null;
  }
})

async function repositionPopover() {
  if (showingDesktopUi.value) {
    await popover.value.reposition();
  }
}

watch(() => store.showAdvanced, async () => {
  if (showingPopover.value)
    await repositionPopover();
});

defineExpose({
  repositionPopover
});

</script>

<style>
.tableCell-popover {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1), 
    0 2px 4px -2px rgba(0, 0, 0, 1)
}
.tableCell-popover .p-overlaypanel-content {
  padding: 20px;
}
</style>